<script setup lang="ts">
import {computed, ref} from 'vue';
import {Check, LoaderCircle, RefreshCw} from '@lucide/vue';
import {ADAC_UPCOMING_CATEGORIES, adacAlert, adacCount, adacHasFindings, adacRows, adacSummary} from '@/amelipro/adacCategories';
import {AdacCategoryCheck, AdacRefusal, adacNeedsNir, adacRetryable} from '@/amelipro/composables/useAdac';
import {portalNir} from '@/amelipro/nir';
import BaseTooltip from '@/core/components/tooltips/BaseTooltip.vue';

/**
 * The ADAC detail: what each category answered for the patient at the counter.
 *
 * It unfolds under the ADAC programme's card, which carries the gist and, on
 * a refusal, its reason: what is here is the rest — every category on a line
 * of its own, or what to do about the refusal. Small type, the card being read
 * first. What is read here is transmitted, never judged: whether a quantity is
 * too much for this patient is the pharmacist's call.
 */
const props = defineProps<{
    /** Where the check as a whole stands. */
    state: 'idle' | 'running' | 'done' | 'failed';
    /** One entry per category asked for. */
    checks: AdacCategoryCheck[];
    /** The gateway that did the work, when it was not this post. */
    via: string | null;
    /** Why the check came to nothing, when it did. */
    refusal: AdacRefusal | null;
}>();

const emit = defineEmits<{
    /** The pharmacist asks for another go. */
    (event: 'retry'): void;
    /** The pharmacist typed the NIR the detection did not give. */
    (event: 'check-with-nir', nir: string): void;
}>();

/** Whether another go is worth offering. */
const canRetry = computed(() => props.state === 'failed' && adacRetryable(props.refusal));

/** The check stopped for want of a NIR the portal would take, which the pharmacist can hand over here. */
const needsNir = computed(() => props.state === 'failed' && adacNeedsNir(props.refusal));

/** What the pharmacist typed — kept in this detail only, gone with it. */
const typedNir = ref('');

const typedNirValid = computed(() => portalNir(typedNir.value) !== '');

/** Each category on its line, then the ones announced but not served yet. */
const lines = computed(() => [
    ...props.checks.map((check) => {
        const summary = check.verdict ? adacSummary(check.category, check.verdict) : null;

        return {
            key: check.category.slug,
            name: check.category.short,
            upcoming: false,
            running: check.state === 'running',
            failed: check.state === 'failed' || (check.state === 'done' && !check.verdict),
            // ADAC's flag wins: a subject it raises is never shown as clear.
            alert: !!check.verdict && adacAlert(check.verdict),
            empty: !!check.verdict && !adacHasFindings(check.verdict) && !adacAlert(check.verdict),
            // Something was reimbursed, and ADAC did not flag it: a figure for
            // the pharmacist to read, not a verdict — marked « to check ».
            reimbursed: !!check.verdict && adacHasFindings(check.verdict) && !adacAlert(check.verdict),
            count: summary?.count.toLocaleString('fr-FR') ?? '',
            detail: summary?.detail ?? '',
            depth: check.verdict?.profondeur ?? '',
            // The reimbursements behind the count, one per row: what was
            // reimbursed (a product, a dosage, or nothing for a single-count
            // category), how much, and the day it last was — read on hover.
            details: check.verdict
                ? adacRows(check.category, check.verdict).map(row => ({
                    label: row.label,
                    amount: adacCount(row.total, check.category),
                    date: row.date ?? '',
                }))
                : [],
        };
    }),
    ...ADAC_UPCOMING_CATEGORIES.map(name => ({
        key: name,
        name,
        upcoming: true,
        running: false,
        failed: false,
        empty: false,
        alert: false,
        reimbursed: false,
        count: '',
        detail: '',
        depth: '',
        details: [],
    })),
]);

const submitNir = (): void => {
    if (typedNirValid.value) {
        emit('check-with-nir', typedNir.value);
    }
};
</script>

<template>
  <div class="text-xs">
    <p
      v-if="via"
      class="pb-1.5 text-gray-dark/60"
    >
      via {{ via }}
    </p>
    <div v-if="needsNir">
      <p class="leading-snug text-gray-dark/70">
        Relevez le NIR du patient dans votre LGO et saisissez-le ici pour lancer la
        vérification. Il n'est ni enregistré ni journalisé.
      </p>
      <div class="mt-1.5 flex items-center gap-2">
        <input
          v-model="typedNir"
          inputmode="numeric"
          maxlength="17"
          autocomplete="off"
          placeholder="13 ou 15 chiffres"
          class="min-w-0 flex-1 rounded-lg border border-gray-200 px-2 py-1"
          @keyup.enter="submitNir"
        >
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-full bg-primary px-3 py-1 font-semibold text-white disabled:opacity-50"
          :disabled="!typedNirValid"
          @click="submitNir"
        >
          Vérifier
        </button>
      </div>
    </div>
    <!-- One line per category, as the counter reads it: what ADAC counted,
         over which period. No verdict of Connect's own: a figure says
         nothing about whether it is too much. Only an empty answer gets its
         check mark, and ADAC's own flag, when it raises one, its « ! ». -->
    <ul
      v-else-if="state !== 'failed'"
      class="divide-y divide-gray-200/70"
    >
      <li
        v-for="line in lines"
        :key="line.key"
        class="flex items-center gap-2 py-1"
      >
        <span
          class="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-white"
          :class="{'bg-green': line.empty, 'bg-orange': line.alert, 'bg-primary': line.reimbursed}"
        >
          <Check
            v-if="line.empty"
            :size="9"
            :stroke-width="3"
          />
          <span
            v-else-if="line.alert"
            class="text-[9px] font-bold leading-none"
          >!</span>
          <span
            v-else-if="line.reimbursed"
            class="text-[9px] font-bold leading-none"
          >?</span>
        </span>
        <span
          class="min-w-20 flex-1 truncate font-semibold"
          :class="line.upcoming ? 'text-gray-dark/50' : 'text-gray-dark'"
        >{{ line.name }}</span>
        <span
          v-if="line.upcoming"
          class="shrink-0 rounded-full bg-neutral px-2 py-px text-[10px] text-gray-dark/60"
        >bientôt</span>
        <LoaderCircle
          v-else-if="line.running"
          class="shrink-0 animate-spin text-gray-dark/60"
          :size="12"
        />
        <span
          v-else-if="line.failed"
          class="shrink-0 text-gray-dark/60"
        >non communiqué</span>
        <span
          v-else-if="line.empty"
          class="shrink-0 text-gray-dark/60"
        >aucun</span>
        <BaseTooltip
          v-else-if="line.details.length"
          side="top"
        >
          <span
            class="min-w-0 shrink cursor-help truncate text-gray-dark underline decoration-dotted decoration-gray-dark/40 underline-offset-2"
          ><span class="font-bold">{{ line.count }}</span> {{ line.detail }}</span>
          <template #content>
            <ul class="space-y-0.5 text-left">
              <li
                v-for="(detail, index) in line.details"
                :key="index"
                class="whitespace-nowrap"
              >
                <span
                  v-if="detail.label"
                  class="font-semibold"
                >{{ detail.label }} — </span>{{ detail.amount }}<span v-if="detail.date"> · le {{ detail.date }}</span>
              </li>
            </ul>
          </template>
        </BaseTooltip>
        <span
          v-else
          class="shrink-0 whitespace-nowrap text-gray-dark"
        ><span class="font-bold">{{ line.count }}</span> {{ line.detail }}</span>
        <span
          v-if="!line.upcoming"
          class="w-11 shrink-0 text-right text-gray-dark/60"
        >{{ line.depth }}</span>
      </li>
    </ul>
    <button
      v-if="canRetry"
      class="flex cursor-pointer items-center gap-1.5 font-medium text-primary underline-offset-2 hover:underline"
      @click="emit('retry')"
    >
      <RefreshCw :size="12" />
      Réessayer
    </button>
  </div>
</template>
